import React from "react";
import { Grid, IconButton } from "@mui/material";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format } from 'date-fns';
import FeatherIcon from "feather-icons-react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../components/auth/AuthContext.js";
import TableComponent from "../../components/table/list.jsx";
import { DATE_FORMAT, LIST_NULL_OPTION_VALUE } from "../../helpers/constant.js";
import Utils from "../../utils";
import CreateCampaignStepTwo from "./create-campaign-step-two.jsx";
import CreateCampaign from "./create-campaign.jsx";
import EditCampaignComponent from "./edit-campaign.jsx";
import PinUnpinConfirmButton from "./pin-unpin-confirm.jsx";
import { AppDataContext } from "../../components/context/app-data-context.jsx";
import { CustomTooltip, LinearProgressWithLabel } from "../../consts.js";
import { getCampaignListAPI } from "../../services/campaign.js";

function Campaigns() {
    const navigate = useNavigate();
    const [value, setValue] = React.useState(null);
    const [newCampDiag, setNewCampDiag] = React.useState(false);
    const [editCampaignDialog, setEditCampaignDialog] = React.useState(false);
    const [editCampaignData, setEditCampaignData] = React.useState({});
    const [spinner, setSpinner] = React.useState(false);
    const [newCampDiag2, setNewCampDiag2] = React.useState(false);
    const [diag2input, setDiag2input] = React.useState(null);
    const [age] = React.useState("");
    const [trackers, setTrackers] = React.useState([]);
    const [companyData, setCompanyData] = React.useState({});
    const [allCampaigns, setAllCampaigns] = React.useState([]);

    const { isAuthenticated, currentNonAdminGroup } = React.useContext(AuthContext);
    const AppDataContextDTO = React.useContext(AppDataContext);
    const [totalRecords, setTotalRecords] = React.useState(0);
    const [pageInfo, setPageInfo] = React.useState({
        currentPage: 0,
        rowsPerPage: 10,
        nextPage: null,
        type: LIST_NULL_OPTION_VALUE,
        direction: LIST_NULL_OPTION_VALUE,
    });

    async function getCompanyName() {
        try {    
            setSpinner(true);
            let companyList = await Utils.genericList("listCompanies", {
                filter: { internalname: { eq: currentNonAdminGroup } },
            });
            companyList = companyList.data.listCompanies.items;
            if (companyList.length === 0) {
                console.error("No company found for the current group:", currentNonAdminGroup);
                setSpinner(false);
                navigate("/", { replace: true });
                return;
            }
            const companyId = companyList[0].id;
            let company = await Utils.genericGet("getCompany", companyId);
            company = company.data.getCompany;
            setCompanyData(company);
            company.trackers = JSON.parse(company.trackers);
            const newTrackers = company.trackers.filter((l) => l.phrases.length > 0);
            setTrackers(newTrackers);
            getCampaignList();
        } catch (error) {
            console.error("Error fetching company name:", error);
        }
    }

    const getCampaignList = async (
        rowsPerPage = pageInfo.rowsPerPage, 
        nextPage = pageInfo.nextPage, 
        type = pageInfo.type, 
        direction = pageInfo.direction
    ) => {
        try {
            setSpinner(true);
            const data = await getCampaignListAPI({
                limit: rowsPerPage,
                lastKey: nextPage,
                type: type === LIST_NULL_OPTION_VALUE ? null : type,
                direction: direction === LIST_NULL_OPTION_VALUE ? null : direction,
            })
            data.items.forEach((item) => item.vs === undefined && (item.vs = 0)); // Ensure vs is defined
            let newData = [...data.items];
            if (nextPage) {
                newData = [...allCampaigns, ...data.items];
            }
            setAllCampaigns([...newData]);
            setPageInfo((prev) => ({ ...prev, nextPage: data.lastKey || null }));
            setTotalRecords(data.totalRecords);
            setSpinner(false);
        } catch (error) {
            console.error("Error fetching campaign list:", error);
            setSpinner(false);
        }
    }

    React.useEffect(() => {
        AppDataContextDTO.clearAppData();
        if(isAuthenticated && currentNonAdminGroup) {
            getCompanyName(); // Fetch company data
        } else {
            console.log("isAuthenticated:", isAuthenticated, "currentNonAdminGroup:", currentNonAdminGroup);
        }
        window.scrollTo(0, 0); // Scroll to top when component mounts
    }, [isAuthenticated, currentNonAdminGroup]);

    const columns = React.useMemo(() => ([
        {
            label: "Campaign/Entity",
            key: "name",
            sortable: true,
            renderItem: (row) => (<>
                <div
                    style={{
                        fontWeight: 500,
                    }}
                >
                    {row.name || "-"}
                </div>
                <div>
                    {row.companyName}
                </div>
            </>)
        },
        {
            label: "Pin",
            key: "pinned",
            sortable: true,
            renderItem: (row) => (<PinUnpinConfirmButton
                data={row}
                onSuccess={(state) => {
                    const _data = [...allCampaigns];
                    for (const campaign of _data) {
                        if (campaign.id === row.id) {
                            campaign.pinned = state;
                            break;
                        }
                    }
                    setAllCampaigns(_data);
                    setPageInfo({
                        ...pageInfo,
                        currentPage: 0,
                        nextPage: null,
                    });
                    getCompanyName();
                }} />)
        },
        {
            label: "Type",
            key: "type",
            sortable: true,
            renderItem: (row) => row.type || "-",
        },
        {
            label: "Direction",
            key: "direction",
            sortable: true,
            renderItem: (row) => row.direction || "-",
        },
        {
            label: "Status",
            key: "active",
            sortable: true,
            renderItem: (row) => (<Chip
                label={row.active ? "Active" : "Inactive"}
                color={row.active ? "success" : "info"}
            />)
        },
        {
            label: "Avg. Vocalysd Score",
            key: "vs",
            sortable: true,
            renderItem: (row) => <LinearProgressWithLabel variant="determinate" value={row.vs || 0} />,
        },
        {
            label: "Date Created",
            key: "createdAt",
            sortable: true,
            renderItem: (row) => (<>
                {format(new Date(row.createdAt), DATE_FORMAT) || "-"}
            </>)
        },
        {
            label: "Edit",
            key: "edit",
            sortable: false,
            renderItem: (row) => (<CustomTooltip title={"Edit"}>
                <IconButton aria-label="Edit" size="small" color="inherit" onClick={async (e) => {
                    e.stopPropagation();
                    setEditCampaignData(row);
                    setEditCampaignDialog(true);
                }}>
                    <FeatherIcon icon="edit-2" size={20} />
                </IconButton>
            </CustomTooltip>)
        },
    ]), [allCampaigns]);

    const headerActionButtons = () => {
        return (
            <Button
                variant="contained"
                onClick={() => setNewCampDiag(true)}
                startIcon={<FeatherIcon icon="folder-plus" size={20} />}
            >
                New Campaign
            </Button>
        )
    }


    const handlePageChange = (_pageInfo) => {
        let nextPage = pageInfo.nextPage;
        if(_pageInfo.REFRESH_DATA) {
            nextPage = null
        }
        setPageInfo({ 
            ...pageInfo,
            nextPage,
            currentPage: _pageInfo.currentPage,
            rowsPerPage: _pageInfo.rowsPerPage
        });
        if(nextPage || _pageInfo.REFRESH_DATA) {
            getCampaignList(_pageInfo.rowsPerPage, nextPage, pageInfo.type, pageInfo.direction); 
        }
    }

    const handleFilter = (filterData) => {
        const newPageInfo = {
            ...pageInfo,
            currentPage: 0,
            nextPage: null,
            type: filterData.type || LIST_NULL_OPTION_VALUE,
            direction: filterData.direction || LIST_NULL_OPTION_VALUE,
        };
        setPageInfo(newPageInfo);
        getCampaignList(newPageInfo.rowsPerPage, null, filterData.type, filterData.direction);
    }

    return (
        <Grid display="flex" flexDirection="column" flexGrow="1" overflow="auto">
            <Grid
                className="vocalysed-home-upadted campaigns"
                container
                spacing={0}
                direction="column"
                flexWrap="nowrap"
                flexGrow={1}
                paddingTop={2}
                paddingX={3}
                paddingBottom={3}
                overflow="auto"
            >
                <TableComponent
                    headerActionButtons={headerActionButtons}
                    spinner={spinner}
                    data={allCampaigns}
                    columns={columns}
                    pagination={true}
                    serverPaginate={true}
                    currentPage={pageInfo.currentPage}
                    hasNextPage={pageInfo.nextPage !== null}
                    totalCount={totalRecords}
                    searchKeys={["name", "companyName", "type", "direction"]}
                    filterTypes={{ campaignType: "type", callDirection: "direction" }}
                    defaultFilterValues={{ campaignType: pageInfo.type, callDirection: pageInfo.direction }}
                    noDataMessage={"No campaigns found."}
                    defaultOptions={{ sortKey: 'pinned', sortOrder: 'asc', rowPerPage: pageInfo.rowsPerPage }}
                    rowClick={(item) => navigate("/campaign?id=" + item.id)}
                    onPageChange={handlePageChange}
                    onFilter={handleFilter}
                />

                {true ? null : (
                    <div style={{ width: "20%", background: "#fff", marginLeft: 10 }}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                flexDirection: "row",
                            }}
                        >
                            <div>Filters</div>
                            <Button variant="text">Reset All</Button>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                flexDirection: "column",
                            }}
                        >
                            <div>Date Range</div>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="From"
                                    value={value}
                                    onChange={(newValue) => {
                                        setValue(newValue);
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                                <DatePicker
                                    label="To"
                                    value={value}
                                    onChange={(newValue) => {
                                        setValue(newValue);
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </LocalizationProvider>
                            <div>Limit To</div>
                            <FormControl fullWidth>
                                <InputLabel id="demo-simple-select-label">Age</InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={age}
                                    label="Entity"
                                >
                                    <MenuItem value={10}>Ten</MenuItem>
                                    <MenuItem value={20}>Twenty</MenuItem>
                                    <MenuItem value={30}>Thirty</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="demo-simple-select-label">Age</InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={age}
                                    label="Campaigns"
                                >
                                    <MenuItem value={10}>Ten</MenuItem>
                                    <MenuItem value={20}>Twenty</MenuItem>
                                    <MenuItem value={30}>Thirty</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="demo-simple-select-label">Age</InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={age}
                                    label="Agents"
                                >
                                    <MenuItem value={10}>Ten</MenuItem>
                                    <MenuItem value={20}>Twenty</MenuItem>
                                    <MenuItem value={30}>Thirty</MenuItem>
                                </Select>
                            </FormControl>
                            <Button variant="outlined">
                                Apply
                            </Button>
                        </div>
                    </div>
                )}
            </Grid>

            {/* Modals */}
            <CreateCampaign
                open={newCampDiag}
                companyData={companyData}
                onClose={(data) => {
                    if (data === null) {
                        setNewCampDiag(false);
                        setDiag2input(data);
                    } else {
                        data.trackers = trackers;
                        setDiag2input(data);
                        setNewCampDiag(false);
                        setNewCampDiag2(true);
                    }
                }}
                allCampaigns={allCampaigns}
            />
            <CreateCampaignStepTwo
                currentNonAdminGroup={currentNonAdminGroup}
                open={newCampDiag2}
                data={diag2input}
                onClose={async (dataResponse) => {
                    if (dataResponse === null) {
                        setNewCampDiag2(false);
                    } else {
                        setDiag2input(null);
                        setNewCampDiag2(false);
                        navigate("/campaign?id=" + dataResponse.data.createCampaign.id);
                    }
                }}
                allCampaigns={allCampaigns}
            />
            <EditCampaignComponent
                open={editCampaignDialog}
                companyData={editCampaignData}
                trackersData={trackers}
                onClose={async (inp) => {
                    if (inp === null) setEditCampaignDialog(false);
                    else {
                        Utils.sendProgressBar(true);
                        setEditCampaignDialog(false);
                        await getCompanyName();
                    }
                }}
                allCampaigns={allCampaigns}
            />
        </Grid>
    );
}

export default Campaigns;